/**
 * Browser-side error reporting (§16).
 *
 * The third leg of the P0 checklist item "Sentry receives a test event from API, edge and
 * web" — the API half is `Integration::handles()` in bootstrap/app.php, the edge half is
 * `nlocal-edge --test-event`.
 *
 * The DSN arrives through the `nlocal-config` meta tag, not `import.meta.env`, so the same
 * `public/build` artifact is promotable between environments (DECISIONS.md D-009).
 */
import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'

import { config } from './config'

/** Whether reporting is actually on. Drives the Settings panel's copy and its test button. */
export function sentryEnabled(): boolean {
  return config.sentry.dsn !== ''
}

export function initObservability(app: App, router: Router): void {
  // No DSN is the normal local state. Initialising anyway would queue events that can never
  // be delivered and make `sentryEnabled()` lie to the Settings panel.
  if (!sentryEnabled()) return

  Sentry.init({
    app,
    dsn: config.sentry.dsn,
    environment: config.sentry.environment,
    tracesSampleRate: config.sentry.traces_sample_rate,
    integrations: [Sentry.browserTracingIntegration({ router })],

    /**
     * Strip anything that could carry a session or a tunnel's traffic.
     *
     * §6.4 treats the session cookie and Authorization header as secrets, and the dashboard
     * is the one place both are in scope at once. Sentry does not send cookies by default,
     * but breadcrumbs record fetch URLs — and the API takes tokens in query strings nowhere,
     * so a token in a URL would be a bug worth not compounding by shipping it to a vendor.
     */
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
        const url = breadcrumb.data?.url
        if (typeof url === 'string' && url.includes('?')) {
          breadcrumb.data = { ...breadcrumb.data, url: url.split('?')[0] }
        }
      }
      return breadcrumb
    },
  })
}

/**
 * Send a deliberate test error, so an operator can prove the wiring from the browser the way
 * `php artisan nlocal:test-observability` proves it from the API.
 *
 * Returns false when there is no DSN, so the caller can say so rather than silently
 * appearing to succeed — the exact failure mode this whole checklist item exists to catch.
 */
export function sendTestEvent(): boolean {
  if (!sentryEnabled()) return false

  Sentry.captureException(
    new Error('nlocal observability test — this exception is intentional and safe to ignore.'),
  )

  return true
}
